import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { badgeVariants } from '@/components/ui/badge-variants';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  PICKUP_COMPARISON_FIELD_LABELS,
  type PickupFieldComparison
} from './pickupReconciliationPolicy';
import {
  fetchPickupDriftInspectorData,
  type PickupDriftInspectorTarget
} from './pickupDriftInspectorService';
import type {
  PickupVerificationMismatch,
  PickupVerificationStatus
} from '@/types/pickupVerification';
import {
  PICKUP_LOAN_CENTER_LABEL,
  PICKUP_OPTIMAL_BLUE_LABEL,
  pickupVerificationSeverity,
  pickupVerificationSeverityClass,
  pickupVerificationShortLabel
} from './pickupVerificationPresentation';

export interface PickupVerificationBadgeValue {
  status: PickupVerificationStatus | null | undefined;
  mismatchFields?: PickupVerificationMismatch[] | null;
  lastError?: string | null;
  retryAfter?: string | null;
  lastVerifiedAt?: string | null;
  canonicalStateChanged?: boolean;
  checking?: boolean;
  lockDurationDays?: number | null;
  obLockPeriodBucket?: number | null;
}

interface PickupVerificationBadgeProps {
  value: PickupVerificationBadgeValue;
  target: PickupDriftInspectorTarget;
  onInspect: (element: HTMLButtonElement) => void;
  className?: string;
}

type DetailState = 'idle' | 'loading' | 'loaded' | 'error';

function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(parsed);
}

function fieldLabel(field: string): string {
  return PICKUP_COMPARISON_FIELD_LABELS[field as keyof typeof PICKUP_COMPARISON_FIELD_LABELS]
    || field.replace(/_/g, ' ');
}

function targetKey(target: PickupDriftInspectorTarget): string {
  return [
    target.obLoanCacheId,
    target.encompassLoanId,
    target.obLoanId,
    target.loanNumber
  ].filter(Boolean).join(':');
}

function sortedConflicts(comparisons: PickupFieldComparison[]): PickupFieldComparison[] {
  return comparisons
    .filter(({ verdict }) => verdict === 'drift')
    .sort((left, right) => {
      if (left.tier !== right.tier) {
        return left.tier === 'blocking' ? -1 : 1;
      }
      return left.label.localeCompare(right.label);
    });
}

export function PickupVerificationBadge({
  value,
  target,
  onInspect,
  className
}: PickupVerificationBadgeProps) {
  const status = value.status;
  const [detailState, setDetailState] = useState<DetailState>('idle');
  const [comparisons, setComparisons] = useState<PickupFieldComparison[] | null>(null);
  const requestId = useRef(0);
  const requestState = useRef<DetailState>('idle');
  const key = targetKey(target);

  useEffect(() => {
    requestId.current += 1;
    requestState.current = 'idle';
    setDetailState('idle');
    setComparisons(null);
  }, [key]);

  const shouldLoadDetails = Boolean(
    status
    && status !== 'verified'
    && (key || value.mismatchFields?.length)
  );

  const loadDetails = useCallback(async () => {
    if (!shouldLoadDetails || requestState.current === 'loading' || requestState.current === 'loaded') {
      return;
    }
    const currentRequest = requestId.current + 1;
    requestId.current = currentRequest;
    requestState.current = 'loading';
    setDetailState('loading');
    try {
      const data = await fetchPickupDriftInspectorData(target);
      if (requestId.current === currentRequest) {
        requestState.current = 'loaded';
        setComparisons(data.comparisons);
        setDetailState('loaded');
      }
    } catch {
      if (requestId.current === currentRequest) {
        requestState.current = 'error';
        setDetailState('error');
      }
    }
  }, [shouldLoadDetails, target]);

  const conflicts = useMemo(
    () => comparisons ? sortedConflicts(comparisons) : [],
    [comparisons]
  );
  const missingCount = useMemo(
    () => comparisons?.filter(({ verdict }) => verdict === 'missing_in_ob').length || 0,
    [comparisons]
  );
  const fallbackMismatchLabels = (value.mismatchFields || [])
    .slice()
    .sort((left, right) => left.tier === right.tier ? 0 : left.tier === 'blocking' ? -1 : 1)
    .map(({ field }) => fieldLabel(field));

  const shortPresentationInput = {
    status,
    mismatchFields: value.mismatchFields,
    lastVerifiedAt: value.lastVerifiedAt,
    canonicalStateChanged: value.canonicalStateChanged,
    checking: value.checking
  };
  const severity = pickupVerificationSeverity(shortPresentationInput);

  const explanation = value.checking
    ? `${PICKUP_OPTIMAL_BLUE_LABEL} verification is running for this loan.`
    : value.canonicalStateChanged
    ? `${PICKUP_LOAN_CENTER_LABEL} changed. Analyze or Refresh OB Cache will verify ${PICKUP_OPTIMAL_BLUE_LABEL} before pricing.`
    : status === 'verified'
      ? `Lock identity verified${value.lastVerifiedAt ? ` ${formatDateTime(value.lastVerifiedAt)}` : ''}.`
      : status === 'verified_with_drift'
        ? 'Pricing-input differences are informational; pricing remains enabled.'
        : status === 'waiting_for_ob_sync'
          ? `Waiting for ${PICKUP_OPTIMAL_BLUE_LABEL} propagation${value.retryAfter ? ` until ${formatDateTime(value.retryAfter)}` : ''}.`
          : status === 'ob_mismatch'
            ? `The lock identity differs. Refresh after correcting ${PICKUP_LOAN_CENTER_LABEL} or ${PICKUP_OPTIMAL_BLUE_LABEL}.`
            : status === 'ob_access_error'
              ? value.lastError || `${PICKUP_OPTIMAL_BLUE_LABEL} acquisition failed; the existing retry and parking policy applies.`
              : 'Canonical verification will run before pricing.';
  const durationNote = value.lockDurationDays
    && value.obLockPeriodBucket
    && value.lockDurationDays !== value.obLockPeriodBucket
    ? `${PICKUP_LOAN_CENTER_LABEL} duration ${value.lockDurationDays}d; ${PICKUP_OPTIMAL_BLUE_LABEL} pricing bucket ${value.obLockPeriodBucket}d.`
    : null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              badgeVariants({ variant: 'outline' }),
              'h-6 max-w-64 cursor-pointer gap-0.5 overflow-hidden text-left text-[10px] font-medium transition-colors hover:border-primary/60 hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              pickupVerificationSeverityClass(severity),
              className
            )}
            onClick={(event) => {
              event.stopPropagation();
              onInspect(event.currentTarget);
            }}
            onPointerEnter={() => void loadDetails()}
            onFocus={() => void loadDetails()}
            aria-label={`View ${PICKUP_OPTIMAL_BLUE_LABEL} comparison for ${target.loanNumber || 'loan'}`}
          >
            <span className="min-w-0 truncate" role="status" aria-live="polite">
              {pickupVerificationShortLabel(shortPresentationInput)}
            </span>
            <ChevronRight
              data-pickup-verification-glyph
              className="h-3 w-3 shrink-0"
              aria-hidden="true"
            />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-80">
          <p className="font-medium">Click to compare {PICKUP_LOAN_CENTER_LABEL} vs {PICKUP_OPTIMAL_BLUE_LABEL}</p>
          <p className="mt-1">{explanation}</p>
          {detailState === 'loading' && <p className="mt-1">Loading current field details…</p>}
          {conflicts.length > 0 && (
            <p className="mt-1">Value conflicts: {conflicts.map(({ label }) => label).join(', ')}.</p>
          )}
          {detailState === 'loaded' && missingCount > 0 && (
            <p className="mt-1">+{missingCount} not in {PICKUP_OPTIMAL_BLUE_LABEL} record.</p>
          )}
          {!comparisons && fallbackMismatchLabels.length > 0 && detailState !== 'loading' && (
            <p className="mt-1">Recorded fields: {fallbackMismatchLabels.join(', ')}.</p>
          )}
          {detailState === 'error' && <p className="mt-1">Current field details could not be loaded.</p>}
          {durationNote && <p className="mt-1">{durationNote}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
